import axios from "axios";
import { supabase } from "../../config/supabaseClient";
import {
  extractLinkedInUsername,
  normalizeLinkedInProfileUrl,
} from "./ContentDatabase";
import { StaticVariables } from "../StaticVariables";
import { isInternalStaffEmail } from "../isInternalStaffEmail";
import { checkTestEmail, isStripeTestModeSync } from "../StripeTestMode";
import { fireAffiliateStorefrontScrape } from "./AffiliateStorefrontScrape";
import {
  isAmazonAffiliateUrl,
  normalizeHttpUrlInput,
} from "../helpers/productBankPlatform";

const slackUrl = StaticVariables().SLACK_URL;

const validateAffiliateUrl = (
  platform: string,
  rawUrl: string
): string | null => {
  const value = normalizeHttpUrlInput(rawUrl);

  if (!value) {
    return "Please enter a URL before connecting.";
  }

  let hostname = "";
  try {
    hostname = new URL(value).hostname.toLowerCase();
  } catch (_error) {
    return "Please enter a valid URL.";
  }

  switch (platform) {
    case "amazon": {
      return isAmazonAffiliateUrl(value)
        ? null
        : "Please enter a valid Amazon URL (e.g. https://www.amazon.com/shop/your-store, amazon.co.uk, or amzn.to).";
    }
    case "shopmy": {
      const isShopmyHost =
        hostname === "shopmy.us" || hostname.endsWith(".shopmy.us");
      return isShopmyHost
        ? null
        : "Please enter a valid ShopMy URL (e.g. https://shopmy.us/collections/your-page).";
    }
    case "ltk": {
      const isLtkHost =
        hostname === "shopltk.com" ||
        hostname.endsWith(".shopltk.com") ||
        hostname === "ltk.app.link" ||
        hostname.endsWith(".ltk.app.link");
      return isLtkHost
        ? null
        : "Please enter a valid LTK URL (e.g. https://www.shopltk.com/explore/your-page).";
    }
    default:
      return null;
  }
};

const sanitizeUrlInput = (value: string) => normalizeHttpUrlInput(value);

const PLATFORM_REQUIRED_MESSAGES: Record<string, string> = {
  instagram: "Please enter an Instagram username before connecting.",
  youtube: "Please enter a YouTube username or URL before connecting.",
  twitter: "Please enter an X username or URL before connecting.",
  tiktok: "Please enter a TikTok username or URL before connecting.",
  linkedin: "Please enter a LinkedIn profile or company page URL before connecting.",
  newsletter: "Please enter a newsletter email before connecting.",
  podcast: "Please enter a podcast show name before connecting.",
  podcastUrl: "Please enter a Spotify show URL before connecting.",
  linkinbio: "Please enter a LinkinBio URL before connecting.",
  website: "Please enter a website URL before connecting.",
  amazon: "Please enter an Amazon URL before connecting.",
  shopmy: "Please enter a ShopMy URL before connecting.",
  ltk: "Please enter an LTK URL before connecting.",
};

const requirePlatformInput = (platform: string, value: unknown) => {
  const trimmedValue = typeof value === "string" ? value.trim() : "";
  if (!trimmedValue) {
    throw new Error(
      PLATFORM_REQUIRED_MESSAGES[platform] ||
        "Please enter a username or URL before connecting."
    );
  }
  return trimmedValue;
};

export async function signupUserFolder(
  name: any,
  username: any,
  email: any,
  phone: any,
  password: any
) {
  try {
    if (!username) {
      return {
        error: true,
        message: "Please fill in all fields",
      };
    }

    const url = "https://dev.thesearchbubble.com/createuser";
    const createNewUserError = await axios.post(
      url,
      {
        //   id: userId,
        email: email,
        password: password,
        name: name,
        username: username,
        // instaUsername: instaUsername,
        phoneNo: phone,
      }
      // {
      //   headers: {
      //     "content-type": "application/json",
      //   },
      // }
    );

    if (createNewUserError.data !== "done") {
      const errorMessage = createNewUserError.data.message || "";
      const duplicateEmailMessage =
        'duplicate key value violates unique constraint "user_email_key"';
      const duplicateUsernameMessage =
        'duplicate key value violates unique constraint "user_username_key"';
      const duplicateEmailError = errorMessage.includes(duplicateEmailMessage);
      const duplicateUsernameError = errorMessage.includes(
        duplicateUsernameMessage
      );

      return {
        error: true,
        message:
          createNewUserError?.data?.message ||
          (duplicateEmailError
            ? "This email is already in use."
            : duplicateUsernameError
              ? "This username is already taken. Please try another one."
              : "Error, please try again later or contact our support team."),
      };
    } else {
      return {
        error: false,
        message: "User created successfully",
      };
    }
  } catch (error: any) {
    return {
      error: true,
      message:
        error?.message ||
        "Error while creating user, please report this error to our support team.",
    };
  }
}

export async function updateSelectedPlatforms(
  selectedPlatforms: any,
  username: any,
  newPlatforms: any
) {
  // for (let [index, platform] of selectedPlatforms.entries()) {

  if (typeof newPlatforms.usernames === "string") {
    newPlatforms.usernames = newPlatforms.usernames.trim();
  }

  newPlatforms.usernames = requirePlatformInput(
    newPlatforms.platform,
    newPlatforms.usernames
  );

  if (newPlatforms.platform === "podcast") {
    newPlatforms.showUrl = requirePlatformInput(
      "podcastUrl",
      newPlatforms.showUrl
    );
  }

  if (newPlatforms.platform === "amazon" || newPlatforms.platform === "shopmy" || newPlatforms.platform === "ltk") {
    const validationError = validateAffiliateUrl(newPlatforms.platform, newPlatforms.usernames);
    if (validationError) {
      throw new Error(validationError);
    }
    newPlatforms.usernames = sanitizeUrlInput(newPlatforms.usernames);
  }

  if (newPlatforms.platform === "instagram") {
    try {
      const API_BASE = StaticVariables().API_BASE;
  
      const api_url = `${API_BASE}/validate-instagram`;
      const response = await axios.post(api_url, {
        username: newPlatforms.usernames,
      });
      if (response?.data?.success == true) {
        await supabase
          .from("user")
          .update({ instagram_username: newPlatforms.usernames })
          .eq("username", username);
      } else {
        throw new Error("Invalid Instagram username");
      }
    } catch (error: any) {
      throw new Error(
        error?.response?.data?.message || "Instagram validation failed"
      );
    }
  } else if (newPlatforms.platform === "linkedin") {
    try {
      if (newPlatforms.usernames) {
        const linkedInProfileUrl = normalizeLinkedInProfileUrl(
          newPlatforms.usernames
        );
        const linkedInUsername = extractLinkedInUsername(linkedInProfileUrl);

        if (!linkedInUsername) {
          throw new Error("Invalid LinkedIn profile URL");
        }

        const API_BASE = StaticVariables().API_BASE;
        const api_url = `${API_BASE}/verify-linkedin`;
        const validationResponse = await axios.post(api_url, {
          username: linkedInProfileUrl,
        });
        if (validationResponse?.data?.success == true) {
          await supabase
            .from("user")
            .update({
              linkedin_user: linkedInUsername,
              is_linkedin_on: true,
            })
            .eq("username", username);
        } else {
          throw new Error("Invalid LinkedIn profile URL");
        }
      }
    } catch (error: any) {
      throw new Error(
        error?.response?.data?.message || "LinkedIn validation failed"
      );
    }
  } else if (newPlatforms.platform === "tiktok") {
    try {
      if (newPlatforms.usernames) {
        const result = newPlatforms.usernames.startsWith("@")
          ? newPlatforms.usernames.slice(1)
          : newPlatforms.usernames;
        const api_url = `${StaticVariables().API_BASE}/verify-tiktok`;
        const validationResponse = await axios.post(api_url, {
          username: result,
        });
        if (validationResponse?.data?.success == true) {
          await supabase
            .from("user")
            .update({ tiktok_user: result, is_tiktok_on: true })
            .eq("username", username);
        } else {
          throw new Error(
            validationResponse?.data?.error || "Invalid TikTok username"
          );
        }
      }
    } catch (error: any) {
      throw new Error(
        error?.response?.data?.error || "TikTok validation failed"
      );
    }
  } else if (newPlatforms.platform === "youtube") {
    if (newPlatforms.usernames) {
      // const youtubeChannelId = await extractYoutubeChannelId(platform.username);
      const newPlatformsUsername = newPlatforms.usernames?.includes("@")
        ? newPlatforms.usernames.slice(1)
        : newPlatforms.usernames;
      try {
        const api_url = `${StaticVariables().API_BASE}/verify-youtube`;

        const validationResponse = await axios.post(api_url, {
          username: newPlatformsUsername,
        });
        if (validationResponse?.data?.success == true) {
          await supabase
            .from("user")
            .update({
              // youtube_playlist_id:
              //   validationResponse?.data?.channelInfo?.uploadPlaylistId,
              youtube_playlist_id:
                validationResponse?.data?.channelInfo?.youtubeChannelUrl,
              youtube_channel_id:
                validationResponse?.data?.channelInfo?.channelId,
              // youtube_channel_id: validationResponse?.data?.channelInfo?.id,
              youtube_username: newPlatformsUsername,
              is_youtube_on: true,
            })
            .eq("username", username);
        } else {
          throw new Error("Invalid YouTube channel");
        }
      } catch (error: any) {
        throw new Error(
          error?.response?.data?.message || "YouTube validation failed"
        );
      }
    }
  } else if (newPlatforms.platform === "newsletter") {
    await supabase
      .from("user")
      .update({
        newsletter_email: newPlatforms.usernames,
        newsletter_website_link: newPlatforms.websiteLink || null,
        is_newsletter_on: true,
      })
      .eq("username", username);
  } else if (newPlatforms.platform === "podcast") {
    try {
      const podcastShowName = newPlatforms.usernames;
      const podcastShowUrl = newPlatforms.showUrl || "";
      const spotifyId = extractSpotifyID(podcastShowUrl);

      const { data, error } = await supabase
        .from("user")
        .update({
          podcast_link: podcastShowUrl,
          podcast_showname: podcastShowName,
          podcast_artist: podcastShowName,
          podcast_id: spotifyId,
          is_podcast_on: true,
        })
        .eq("username", username);
    } catch (error: any) {
      throw new Error(
        error?.response?.data?.message || "Podcast validation failed"
      );
    }
  } else if (newPlatforms.platform === "twitter") {
    if (newPlatforms.usernames) {
      try {
        const result = newPlatforms.usernames.startsWith("@")
          ? newPlatforms.usernames.slice(1)
          : newPlatforms.usernames;

        const api_url = `${StaticVariables().API_BASE}/verify-twitter`;
        const validationResponse = await axios.post(api_url, {
          username: result,
        });
        if (validationResponse?.data?.success == true) {
          await supabase
            .from("user")
            .update({
              twitter_user: newPlatforms.usernames,
              is_twitter_on: true,
            })
            .eq("username", username);
        } else {
          throw new Error("Invalid Twitter username");
        }
      } catch (error: any) {
        throw new Error(
          error?.response?.data?.message || "Twitter validation failed"
        );
      }
    }
  } else if (newPlatforms.platform === "linkinbio") {
    await supabase
      .from("user")
      .update({ linkinbio: newPlatforms.usernames })
      .eq("username", username);
  } else if (newPlatforms.platform === "amazon") {
    await supabase
      .from("user")
      .update({ social_amazon: newPlatforms.usernames, social_affiliate_amazon: newPlatforms.usernames })
      .eq("username", username);
    fireAffiliateStorefrontScrape({
      username,
      platform: "Amazon",
      storefrontUrl: newPlatforms.usernames,
    });
  } else if (newPlatforms.platform === "shopmy") {
    await supabase
      .from("user")
      .update({ social_shopmy: newPlatforms.usernames, social_affiliate_shopmy: newPlatforms.usernames })
      .eq("username", username);
    fireAffiliateStorefrontScrape({
      username,
      platform: "ShopMy",
      storefrontUrl: newPlatforms.usernames,
    });
  } else if (newPlatforms.platform === "ltk") {
    await supabase
      .from("user")
      .update({ social_ltk: newPlatforms.usernames, social_affiliate_ltk: newPlatforms.usernames })
      .eq("username", username);
    fireAffiliateStorefrontScrape({
      username,
      platform: "LTK",
      storefrontUrl: newPlatforms.usernames,
    });
  } else if (newPlatforms.platform === "website") {
    await supabase
      .from("user")
      .update({ social_website: newPlatforms.usernames })
      .eq("username", username);
  }
}
// }

export async function IntegratePlatforms(
  selectedPlatforms: any,
  username: any
) {
  // const API_BASE = "http://52.9.65.91:3006";
  const API_BASE = "https://demo.thesearchbubble.com";
  // const API_BASE = "https://dev.thesearchbubble.com";
  // username="ch_12"
  // selectedPlatforms = [
  //   {
  //     platform: "instagram",
  //     username: "akriti_3109",
  //   },
  //   {
  //     platform: "youtube",
  //     username:"onepotatobox6281"

  //   },
  //   {
  //     platform: "tiktok",
  //     username: "deepa_toks",
  //   },
  //   {
  //     platform: "linkedin",
  //     username: "mahima-dariyani",
  //   },
  //   {
  //     platform: "newsletter",
  //     username: "mahidariyani05@gmail.com",
  //     websiteLink: "https://www.thesearchbubble.com",
  //   },
  //   {
  //     platform: "podcast",
  //     username: "the money",
  //     showUrl: "https://open.spotify.com/show/10BfU2D8gXUKJ4mJRleBj1",
  //     showId: "10BfU2D8gXUKJ4mJRleBj1",
  //   },
  //   {
  //     platform: "twitter",
  //     username: "akriti_3109",
  //   },
  // ];

  const { data } = await supabase
    .from("user")
    .select("*")
    .eq("username", username);

  // // const platformNames = selectedPlatforms.map((item: any) => item.platform).join(",");
  // const platformNames = selectedPlatforms
  //   .filter(
  //     (item: any) =>
  //       !["amazon", "shopmy", "ltk", "Amazon", "ShopMy", "LTK"].includes(
  //         item.platform
  //       )
  //   )
  //   .map((item: any) => item.platform)
  //   .join(",");
  // const { data: updatedPlatforms } = await supabase
  //   .from("user")
  //   .update({
  //     platforms_onboarded: platformNames,
  //     subscription_type: "pro",
  //   })
  //   .eq("username", username);
  const user_id = data?.[0]?.user_id;
  for (let [index, platform] of selectedPlatforms.entries()) {
    if (platform.platform === "instagram") {
      const highlightSetupUrl = `https://dev.thesearchbubble.com/highlightsetup-onboarding`;
      // const highlightSetupUrl = `${API_BASE}/highlightsetup-onboarding`;
      axios.post(highlightSetupUrl, {
        id: user_id,
        name: username,
        from: "content",
        instaUsername: platform.username,
      });
      // const demoSetupUrl = `https://dev.thesearchbubble.com/datascrapper-onboarding`;
      const demoSetupUrl = `${API_BASE}/datascrapper-onboarding`;
      const resThumbnail = axios.post(demoSetupUrl, {
        id: user_id,
        name: username,
        from: "content",
        instaUsername: platform.username,
      });
      // await axios.post(
      //   slackUrl,
      //   {
      //     userId: user_id,
      //     user: username,
      //     channel: channel,
      //     msg: `Instagram Integration
      //     Username: ${username},

      //     Instagram Handle: ${platform.username}
      //     User ID: ${user_id}`,
      //   },
      //   {
      //     headers: {
      //       "Content-Type": "application/json",
      //     },
      //   }
      // );
      // }, 2000);
    } else if (platform.platform === "youtube") {
      const url = `${API_BASE}/fetch/youtube`;
      // const url = `${API_BASE}/api/youtube/storeVideosByChannelId`;
      const resServer: any = axios.post(url, {
        userId: user_id,
        channelId: platform.channelId,
        username: username,
      });

      // const urlYtShorts = `https://dev.thesearchbubble.com/api/youtube/shorts`;
      // // const urlYtShorts = `${API_BASE}/api/youtube/shorts`;
      // const YTShorts = axios.post(urlYtShorts, {
      //   userId: user_id,
      //   channelId: platform.channelId,
      //   username: username,
      // });

      // await axios.post(
      //   slackUrl,
      //   {
      //     userId: user_id,
      //     user: username,
      //     channel: channel,
      //     msg: `YouTube Integration
      //     Username: ${username},

      //    YouTube Username: ${platform.username}
      //    User ID: ${user_id}`,
      //   },
      //   {
      //     headers: {
      //       "Content-Type": "application/json",
      //     },
      //   }
      // );
    } else if (platform.platform === "tiktok") {
      // const url = `https://dev.thesearchbubble.com/integrate/tiktok`;
      const url = `${API_BASE}/integrate/tiktok`;
      axios.post(url, {
        id: user_id,
        name: username,
        username: username,
        tiktokUsername: platform.username,
      });
      // await axios.post(
      //   slackUrl,
      //   {
      //     userId: user_id,
      //     user: username,
      //     channel: channel,
      //     msg: `TikTok Integration
      //     Username: ${username},

      //     TikTok Handle: ${platform.username}
      //     User ID: ${user_id}`,
      //   }
      //   // {
      //   //   headers: {
      //   //     "Content-Type": "application/json",
      //   //   },
      //   // }
      // );
    } else if (platform.platform === "linkedin") {
      // const url = `https://dev.thesearchbubble.com/fetch/linkedin`;
      const url = `${API_BASE}/fetch/linkedin`;

      const resServer = axios.post(url, {
        userId: user_id,
        user: username,
        linkedInUsername: platform.username,
      });

      // await axios.post(
      //   slackUrl,
      //   {
      //     userId: user_id,
      //     user: username,
      //     channel: channel,
      //     msg: `LinkedIn Integration
      //     Username: ${username},

      //     LinkedIn Username: ${platform.username}
      //     User ID: ${user_id}`,
      //   },
      //   {
      //     headers: {
      //       "Content-Type": "application/json",
      //     },
      //   }
      // );
    } else if (platform.platform === "newsletter") {
      const url = `${API_BASE}/api/user/store/newsletter`;

      const resServer = axios.post(url, {
        userId: user_id,
        user: username,
        email: platform.username,
      });

      // await axios.post(
      //   slackUrl,
      //   {
      //     userId: user_id,
      //     user: username,
      //     channel: channel,
      //     msg: `Newsletter Integration
      //     Username: ${username},

      //     Email: ${platform.username}
      //     Website Link: ${platform.websiteLink || "Not provided"}
      //     User ID: ${user_id}`,
      //   },
      //   {
      //     headers: {
      //       "Content-Type": "application/json",
      //     },
      //   }
      // );
    } else if (platform.platform === "podcast") {
      const url = `${API_BASE}/fetch/podcast`;

      let res: any = axios.post(url, {
        user: username,
        user_id: user_id,
        showName: platform.username,
        id: platform.showId,
      });

      // await axios.post(
      //   slackUrl,
      //   {
      //     userId: user_id,
      //     user: username,
      //     channel: channel,
      //     msg: `Podcast Integration
      //     Username: ${username},

      //     Podcast Show Name: ${platform.username}
      //     Podcast Show URL: ${platform.showUrl || "Not provided"}
      //     User ID: ${user_id}`,
      //   },
      //   {
      //     headers: {
      //       "Content-Type": "application/json",
      //     },
      //   }
      // );
    } else if (platform.platform === "twitter") {
      const url = `${API_BASE}/fetch/twitter`;

      const response = axios.post(url, {
        userId: user_id,
        user: username,
        twitterUser: platform.username,
      });
      // await axios.post(
      //   slackUrl,
      //   {
      //     userId: user_id,
      //     user: username,
      //     channel: channel,
      //     msg: `Twitter Integration
      //     Username: ${username},

      //     Twitter Handle: ${platform.username}
      //     User ID: ${user_id}`,
      //   },
      //   {
      //     headers: {
      //       "Content-Type": "application/json",
      //     },
      //   }
      // );
    } else if (
      platform.platform === "amazon" ||
      platform.platform === "Amazon"
    ) {
      fireAffiliateStorefrontScrape({
        username,
        userId: user_id,
        platform: "Amazon",
        storefrontUrl: platform.username,
      });
    } else if (
      platform.platform === "shopmy" ||
      platform.platform === "ShopMy"
    ) {
      fireAffiliateStorefrontScrape({
        username,
        userId: user_id,
        platform: "ShopMy",
        storefrontUrl: platform.username,
      });
    } else if (platform.platform === "ltk" || platform.platform === "LTK") {
      fireAffiliateStorefrontScrape({
        username,
        userId: user_id,
        platform: "LTK",
        storefrontUrl: platform.username,
      });
    }
  }
}

type LimitedSyncRequest = {
  platform:
    | "instagram"
    | "youtube"
    | "twitter"
    | "tiktok"
    | "linkedin"
    | "newsletter"
    | "podcast";
  username: string;
  userId?: string;
  daysToSync?: string | number | null;
  platformUsername?: string;
};

/** Same host/path as `ConnectInstagramStep` — populates link-in-bio user fields from Instagram. */
const PROFILESETUP_ONBOARDING_URL =
  "https://dev.thesearchbubble.com/profilesetup-onboarding";

export type OnboardingUserProfileScrapeRow = {
  is_instagram_on?: boolean | null;
  instagram_username?: string | null;
  description?: string | null;
  storage_url?: string | null;
  picture?: string | null;
};

/**
 * Detect incomplete profile scrape: Instagram is on and we have a handle, but the
 * scraped fields the follower page reads (`description`, `storage_url` / `picture`)
 * are still empty. (There is no separate client-side scraper flag in this repo.)
 */
export function userRowNeedsProfileSetupRetry(
  row: OnboardingUserProfileScrapeRow | null | undefined
): boolean {
  if (!row) return false;
  const ig =
    typeof row.instagram_username === "string"
      ? row.instagram_username.trim()
      : "";
  if (!row.is_instagram_on || !ig) return false;

  const hasPic = Boolean(
    String(row.storage_url || row.picture || "").trim()
  );
  const hasBio = Boolean(String(row.description ?? "").trim());

  return !hasPic || !hasBio;
}

export async function postProfileSetupOnboarding(
  siteUsername: string,
  instaUsername: string
): Promise<boolean> {
  try {
    const res = await axios.post(PROFILESETUP_ONBOARDING_URL, {
      name: siteUsername,
      from: "content",
      instaUsername,
    });
    return Boolean((res as any)?.data?.success);
  } catch (_e) {
    return false;
  }
}

const profileSetupRecoveryInFlight = new Map<string, Promise<void>>();

/**
 * Re-run profile setup when the DB row still lacks scraped avatar/bio after the first
 * attempt (e.g. transient scraper failure). Safe to call multiple times; coalesced per username.
 */
export async function ensureOnboardingProfileSetupStored(
  siteUsername: string
): Promise<void> {
  if (!siteUsername) return;

  let pending = profileSetupRecoveryInFlight.get(siteUsername);
  if (pending) return pending;

  pending = (async () => {
    try {
      const { data, error } = await supabase
        .from("user")
        .select(
          "instagram_username, is_instagram_on, description, storage_url, picture"
        )
        .eq("username", siteUsername)
        .maybeSingle();

      if (error || !data) return;
      if (!userRowNeedsProfileSetupRetry(data as OnboardingUserProfileScrapeRow))
        return;

      const ig = String((data as any).instagram_username || "").trim();
      if (!ig) return;

      const ok = await postProfileSetupOnboarding(siteUsername, ig);
      if (!ok) {
        console.warn(
          "[onboarding] profilesetup-onboarding retry did not return success",
          { siteUsername }
        );
      }
    } finally {
      profileSetupRecoveryInFlight.delete(siteUsername);
    }
  })();

  profileSetupRecoveryInFlight.set(siteUsername, pending);
  return pending;
}

export async function triggerLimitedPlatformSync(req: LimitedSyncRequest) {
  const daysToSync = 7;
  if (!daysToSync) return;

  // Limited IG day-syncs intentionally hit demo (not StaticVariables/dev).
  const API_BASE = "https://demo.thesearchbubble.com";
  const username = req.username;

  let userId = req.userId;
  let platformUsername = req.platformUsername;
  let youtubeChannelId: string | null = null;
  let podcastId: string | null = null;

  try {
    // Fetch any missing identifiers from the user row.
    const { data: userRow } = await supabase
      .from("user")
      .select(
        "user_id, instagram_username, youtube_channel_id, youtube_username, twitter_user, tiktok_user, linkedin_user, newsletter_email, podcast_id, podcast_artist, podcast_showname, podcast_link"
      )
      .eq("username", username)
      .single();

    userId = userId || (userRow as any)?.user_id || "";
    youtubeChannelId = (userRow as any)?.youtube_channel_id ?? null;
    podcastId = (userRow as any)?.podcast_id ?? null;

    if (!platformUsername) {
      switch (req.platform) {
        case "instagram":
          platformUsername = (userRow as any)?.instagram_username ?? "";
          break;
        case "youtube":
          platformUsername = (userRow as any)?.youtube_username ?? "";
          break;
        case "twitter":
          platformUsername = (userRow as any)?.twitter_user ?? "";
          break;
        case "tiktok":
          platformUsername = (userRow as any)?.tiktok_user ?? "";
          break;
        case "linkedin":
          platformUsername = (userRow as any)?.linkedin_user ?? "";
          break;
        case "newsletter":
          platformUsername = (userRow as any)?.newsletter_email ?? "";
          break;
        case "podcast":
          platformUsername =
            (userRow as any)?.podcast_showname ??
            (userRow as any)?.podcast_artist ??
            "";
          break;
      }
    }
  } catch (_e) {
    // If we can't fetch identifiers, fall back to whatever was provided.
  }

  if (!userId) return;
  if (!platformUsername) return;

  switch (req.platform) {
    case "instagram": {
      const storySetupUrl = `${API_BASE}/integrate/story-day`;
      const demoSetupUrl = `${API_BASE}/datascrapper-onboarding-day`;
      const ONBOARDING_LOAD_TIMER_STORAGE_KEY = (u: string) =>
        `onboarding_load_timer_seconds_v1:${u}`;

      const demoPromise = axios
        .post(demoSetupUrl, {
          id: userId,
          name: username,
          from: "content",
          instaUsername: platformUsername,
          daysToSync,
        })
        .then((res) => {
          const loadTimer = (res as any)?.data?.loadTimer;
          if (typeof loadTimer === "number" && loadTimer > 0) {
            try {
              localStorage.setItem(ONBOARDING_LOAD_TIMER_STORAGE_KEY(username), String(loadTimer));
            } catch (_e) {
              // ignore
            }
          }
          return res;
        })
        .catch(() => null);

      void Promise.allSettled([
        demoPromise,
        axios
          .post(storySetupUrl, { username, from: "content", skipOcr: true })
          .catch(() => null),
      ]);
      return;
    }
    // NOTE: Platform-specific "day" sync calls (YT/TikTok/Twitter/LinkedIn/Podcast/etc)
    // have been intentionally removed from the frontend in favor of a single
    // orchestrator call: POST /api/day-sync-platforms.
    default:
      return;
  }
}

export async function disconnectPlatform(platform: string, username: string) {
  try {
    const updates: { [key: string]: any } = {};

    switch (platform) {
      case "instagram":
        updates.instagram_username = null;
        break;
      case "youtube":
        updates.youtube_username = null;
        updates.youtube_channel_id = null;
        updates.youtube_playlist_id = null;
        updates.show_youtube = false;
        updates.is_youtube_on = false;
        break;
      case "twitter":
        updates.twitter_user = null;
        updates.is_twitter_on = false;
        break;
      case "tiktok":
        updates.tiktok_user = null;
        updates.is_tiktok_on = false;
        break;
      case "linkedin":
        updates.linkedin_user = null;
        updates.is_linkedin_on = false;
        break;
      case "newsletter":
        updates.newsletter_email = null;
        updates.newsletter_website_link = null;
        updates.is_newsletter_on = false;
        break;
      case "podcast":
        updates.podcast_link = null;
        updates.podcast_showname = null;
        updates.podcast_id = null;
        updates.is_podcast_on = false;
        break;
      case "linkinbio":
        updates.linkinbio = null;
        break;
      case "amazon":
        updates.social_amazon = null;
        updates.social_affiliate_amazon = null;
        break;
      case "shopmy":
        updates.social_shopmy = null;
        updates.social_affiliate_shopmy = null;
        break;
      case "ltk":
        updates.social_ltk = null;
        updates.social_affiliate_ltk = null;
        break;
      case "website":
        updates.social_website = null;
        updates.social_affiliate_website = null;
        break;
    }

    const { error } = await supabase
      .from("user")
      .update(updates)
      .eq("username", username);

    if (error) throw error;
    return { success: true };
  } catch (error: any) {
    throw new Error(`Failed to disconnect ${platform}: ${error.message}`);
  }
}
export const extractSpotifyID = (url: string) =>
  (url.match(/spotify\.com\/show\/([a-zA-Z0-9]+)/) || [])[1] || null;

export const validateSpotifyPodcastUrl = (url: string) => {
  const spotifyId = extractSpotifyID(url);

  if (/spotify\.com\/episode\//i.test(url)) {
    return {
      error: true,
      message: "This is an episode link. Please enter a valid Podcast link.",
    };
  }

  if (!spotifyId) {
    return {
      error: true,
      message: "Invalid Spotify podcast URL.",
    };
  }

  return {
    error: false,
    spotifyId,
  };
};

export const updateUserSocialLinks = async (
  username: string,
  socialData: any
) => {
  try {
    const { data, error } = await supabase
      .from("user")
      .update({
        social_website: socialData.website,
        linkinbio: socialData.linkinbio,
        social_ltk: socialData.ltk,
        social_shopmy: socialData.shopmy,
        social_amazon: socialData.amazon,
        social_affiliate_amazon: socialData.amazon,
        social_affiliate_shopmy: socialData.shopmy,
        social_affiliate_ltk: socialData.ltk,
      })
      .eq("username", username);

    if (error) throw error;
    return { data, error: null };
  } catch (error) {
    return { data: null, error };
  }
};



type ExtractUsernameResult = {
  username: string;
  error: string | null;
};

const PLATFORM_PATTERNS: Record<string, RegExp[]> = {
  youtube: [
    /^(?:https?:\/\/)?(?:www\.)?youtube\.com\//i,
    /^(?:https?:\/\/)?(?:www\.)?youtu\.be\//i,
  ],
  twitter: [
    /^(?:https?:\/\/)?(?:www\.)?twitter\.com\//i,
    /^(?:https?:\/\/)?(?:www\.)?x\.com\//i,
  ],
  tiktok: [
    /^(?:https?:\/\/)?(?:www\.)?tiktok\.com\//i,
  ],
};

const PLATFORM_LABELS: Record<string, string> = {
  youtube: "YouTube",
  twitter: "Twitter",
  tiktok: "TikTok",
};

export function extractUsername(input: any, platform?: string): ExtractUsernameResult {
  if (!input || typeof input !== "string") {
    return { username: "", error: "Enter a valid username or URL" };
  }

  const trimmedInput = input.trim();
  const lowerCaseInput = trimmedInput.toLowerCase();
  const platformPatterns = platform ? PLATFORM_PATTERNS[platform] : undefined;

  if (trimmedInput.includes("http") && platformPatterns?.length) {
    const matchesPattern = platformPatterns.some((pattern) => pattern.test(lowerCaseInput));
    if (!matchesPattern) {
      const platformLabel = PLATFORM_LABELS[platform ?? ""] ?? "platform";
      return { username: "", error: `Please enter a valid ${platformLabel} URL` };
    }
  }

  let username = trimmedInput;

  // Handle @username directly
  if (username.startsWith("@")) {
    username = username.slice(1);
  }

  // Handle URLs
  if (username.includes("http")) {
    try {
      const normalizedUrl = username.startsWith("http") ? username : `https://${username}`;
      const url = new URL(normalizedUrl);
      const segments = url.pathname.split("/").filter(Boolean);

      if (segments.length > 0) {
        let candidate = segments[segments.length - 1];

        if (platform === "tiktok" || platform === "youtube") {
          const handleSegment = segments.find((segment) => segment.startsWith("@"));
          if (handleSegment) {
            candidate = handleSegment.slice(1);
          }
        }

        if (platform === "youtube" && candidate === "channel" && segments.length > 1) {
          candidate = segments[segments.length - 1];
        }

        username = candidate;
      }
    } catch {
      // Ignore URL parsing errors and fall back to regex extraction
    }
  }

  // Fallback regex extraction if needed
  if (!username || username === trimmedInput) {
    const match = trimmedInput.match(/(?:@|\/)([A-Za-z0-9._-]+)/);
    if (match) {
      username = match[1];
    }
  }

  // Remove query parameters or fragments if present
  username = username.split("?")[0].split("#")[0];

  // Final cleanup of leading @ or trailing slashes
  username = username.replace(/^@/, "").replace(/\/$/, "");

  return { username, error: null };
}

// export async function sendDemoRequest(
//   selectedPlatforms: any,
//   username: any,
//   formData: any,
//   instagramUsername: any
// ) {
//   const { data } = await supabase
//     .from("user")
//     .select("*")
//     .eq("username", username);
//   const user_id = data?.[0]?.user_id;
//   const row = data?.[0].stripe_detail;
//   const subscriptionAmounts = row.subscriptions.map((sub: any) => sub.amount_dollars);

// // ✅ Calculate total subscription amount
// const totalSubscriptionAmount = row.subscriptions.reduce(
//   (sum: any, sub: any) => sum + (sub.amount_dollars ?? 0),
//   0
// );

//   const blockedEmail = `creatorops+${instagramUsername}@gmail.com`;

//   // Skip Slack notification if it's a test/demo email
//   if (formData?.email?.toLowerCase().includes("creatorops+")) {
//     return;
//   } else {
//     const channel = "#full-account-setup";

//     const contentPlatformsList = [
//       "instagram",
//       "youtube",
//       "tiktok",
//       "twitter",
//       "linkedin",
//       "newsletter",
//       "podcast",
//     ];

//     const affiliatePlatformsList = ["amazon", "shopmy", "ltk"];

//     // Categorize the selected platforms
//     const contentPlatforms = selectedPlatforms.filter((platform: any) =>
//       contentPlatformsList.includes(platform.platform)
//     );

//     const affiliatePlatforms = selectedPlatforms.filter((platform: any) =>
//       affiliatePlatformsList.includes(platform.platform)
//     );

//     // Format message for Slack or other notification systems
//     const formattedMessage = `
// Full Account Set-Up Needed:
// Email: ${formData?.email || "Not provided"}
// Username: ${username}
// IG: ${instagramUsername || "Not provided"}

// Content Platforms:
// ${contentPlatforms.map((p: any) => `${p.platform}: ${p.username}`).join("\n")}

// Affiliate Platforms:
// ${affiliatePlatforms.map((p: any) => `${p.platform}: ${p.username}`).join("\n")}
// `;

//     // Uncomment and update the axios code when ready to send to Slack or other services
//     await axios.post(
//       slackUrl,
//       {
//         userId: user_id,
//         user: username,
//         channel: channel,
//         msg: formattedMessage,
//       },
//       {
//         headers: {
//           "Content-Type": "application/json",
//         },
//       }
//     );
//   }
// }

export async function sendDemoRequest(
  selectedPlatforms: any,
  username: any,
  formData: any,
  instagramUsername: any
) {
  const { data } = await supabase
    .from("user")
    .select("*")
    .eq("username", username);
  const user_id = data?.[0]?.user_id;


  // Parse stripe_detail if it's a string, otherwise use as is
  let row = data?.[0]?.stripe_detail;
  if (typeof row === "string") {
    try {
      row = JSON.parse(row);
    } catch (error) {
      console.error("Error parsing stripe_detail:", error);
      row = null;
    }
  }


  // ✅ Get latest subscription by timestamp and fetch its plan + amount
  const subscriptions: any[] = Array.isArray(row?.subscriptions)
    ? row.subscriptions
    : [];


  const latestSubscription = subscriptions
    .map((sub: any) => {
      const ts = new Date(
        sub?.last_updated || sub?.current_period_start || sub?.subscription_created || 0
      ).getTime();
      return { ...sub, _ts: isNaN(ts) ? 0 : ts };
    })
    .sort((a: any, b: any) => b._ts - a._ts)[0];

  const latestPlanName: string = latestSubscription?.plan || "N/A";
  const latestAmountDollars: number = Number(latestSubscription?.amount_dollars ?? 0);

  // ✅ Fallback to stripe_users.details when stripe_detail is null/empty
  let planDetailsName: string = "";
  let planDetailsAmountDollars: number = 0;
  if (!row || !Array.isArray(row?.subscriptions) || row.subscriptions.length === 0) {
    try {
      const userEmail = data?.[0]?.email || formData?.email;
      if (userEmail) {
        const { data: stripeUser, error: stripeUserErr } = await supabase
          .from("stripe_users")
          .select("details")
          .eq("email", userEmail)
          .maybeSingle();
        if (stripeUserErr || !stripeUser) {
          // No stripe_users row for this email — skip quietly (common in local/test).
        } else {
        let details: any = stripeUser?.details;
        if (typeof details === "string") {
          try {
            details = JSON.parse(details);
          } catch (_) {
            details = null;
          }
        }
        const pd = details?.plan_details;
        if (pd) {
          planDetailsName = pd?.name || "";
          const amountCents = Number(pd?.amount ?? 0);
          planDetailsAmountDollars = amountCents
            ? Math.round((amountCents / 100) * 100) / 100
            : 0;
        }
        }
      }
    } catch (_err) {
      // ignore fallback errors; retain defaults
    }
  }

  const finalPlanName: string = latestPlanName !== "N/A" ? latestPlanName : (planDetailsName || "N/A");
  const finalAmountDollars: number = latestAmountDollars || planDetailsAmountDollars || 0;

  const email_check = data?.[0]?.email || formData?.email;
  // Any username starting with "sbtesting" is treated as an internal QA
  // account and must not page the #full-account-setup channel.
  const isInternalTestUsername = typeof username === "string"
    && username.trim().toLowerCase().startsWith("sbtesting");

  // Skip Slack for internal/QA accounts:
  // - usernames starting with "sbtesting"
  // - creatorops+… demo emails
  // - @searchbubble.com / @browsable.ai / *sbtesting* local-part (isInternalStaffEmail)
  if (
    email_check?.toLowerCase().includes("creatorops+") ||
    isInternalTestUsername ||
    isInternalStaffEmail(String(email_check || ""))
  ) {
    return;
  } else {
    const channel = "#full-account-setup";

    const contentPlatformsList = [
      "instagram",
      "youtube",
      "tiktok",
      "twitter",
      "linkedin",
      "newsletter",
      "podcast",
    ];

    const affiliatePlatformsList = ["amazon", "shopmy", "ltk"];

    // Categorize the selected platforms
    const contentPlatforms = selectedPlatforms.filter((platform: any) =>
      contentPlatformsList.includes(platform.platform)
    );

    const affiliatePlatforms = selectedPlatforms.filter((platform: any) =>
      affiliatePlatformsList.includes(platform.platform)
    );

    // ✅ Add plan type + amount to Slack message
    const formattedMessage = `
Full Account Set-Up Needed:
Email: ${ data?.[0]?.email || formData?.email || "Not provided"}
Username: ${username}
IG: ${instagramUsername || "Not provided"}
Plan: ${finalPlanName} ($${finalAmountDollars})

Content Platforms:
${contentPlatforms.map((p: any) => `${p.platform}: ${p.username}`).join("\n")}

Affiliate Platforms:
${affiliatePlatforms.map((p: any) => `${p.platform}: ${p.username}`).join("\n")}
`;


    // Uncomment and update the axios code when ready to send to Slack or other services
    await axios.post(
      slackUrl,
      {
        userId: user_id,
        user: username,
        channel: channel,
        msg: formattedMessage,
      },
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
  }
}


export async function triggerWelcomeEmail(data: any) {
  const demoSetupUrl = `https://dev.thesearchbubble.com/welcome-email-trigger`;
  const resThumbnail = axios.post(demoSetupUrl, {
    data: data,
  });
}

export async function triggerCompleteSetupEmail(
  username: string,
  formData: any
) {
  const demoSetupUrl = `https://dev.thesearchbubble.com/complete-setup-trigger`;
  const resThumbnail = axios.post(demoSetupUrl, {
    username: username,
    formData: formData,
  });
}

export async function updateUserStripeDetail(email: string) {
  //  get stripe detail from stripe_users table
  const { data, error } = await supabase
    .from("stripe_users")
    .select("*")
    .eq("email", email)
    .maybeSingle();

  if (error || !data) {
    return null;
  }

  // convert stripe_detail to json
  let stripeDetail: any = data.details;
  if (typeof stripeDetail === "string") {
    try {
      stripeDetail = JSON.parse(stripeDetail);
    } catch (_e) {
      return null;
    }
  }

  const customerId =
    stripeDetail?.customer_id || stripeDetail?.stripe_customer_id;
  if (!customerId) return null;

  const userDetailsUrl = `${StaticVariables().API_BASE}/api/stripe/update-user-stripe-detail`;
  try {
    return await axios.post(userDetailsUrl, {
      customer_id: customerId,
      testMode: checkTestEmail(email) || isStripeTestModeSync(),
    });
  } catch (e) {
    console.warn("[updateUserStripeDetail] request failed:", e);
    return null;
  }
}

export async function updateZapier(email: string, userId: string) {
  const zapierDetailsUrl = `https://dev.thesearchbubble.com/api/zapier/post-onboarding-notification`;

  try {
    return await axios.post(zapierDetailsUrl, {
      email: email,
      userId: userId,
    });
  } catch (e) {
    console.warn("[updateZapier] request failed:", e);
    return null;
  }
}

export async function updateStripeDetail(username: string, email: string) {
  const { data, error } = await supabase
    .from("stripe_users")
    .select("*")
    .eq("email", email);

  if (data && data.length > 0) {

    // Parse the details JSON string
    const stripeDetail =
      typeof data[0].details === "string"
        ? JSON.parse(data[0].details)
        : data[0].details;


    await supabase
      .from("user")
      .update({
        stripe_detail: stripeDetail,
      })
      .eq("email", email);

    return stripeDetail;
  } else {
    return null;
  }
}
